import { useState, type ReactNode } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";

import { cx } from "@/lib/cx";
import { control, type ControlContext, type ControlSize } from "@/ui/control";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, SelectorIcon } from "@/ui/icons";
import { menuRow, MENU_TONES, popperMaterial } from "./Menu";
import { LAYER } from "./surface";
import { useBackClose } from "./useBackClose";

/**
 * A select that belongs to this app.
 *
 * UI-REVIEW P14: one raw `<select>` and two raw `<input type="date">` were the
 * only elements in the product with system chrome, sitting next to a carefully
 * built segmented control. Everyone notices that even if they cannot name it.
 *
 * The trigger reuses {@link control} rather than restating its classes, so a
 * select and the text field beside it are the same height and sit on the same
 * background — which is the entire reason `control.ts` exists, and IOS-DESIGN
 * §5.1 keeps that abstraction verbatim.
 *
 * ## What changed in the iOS pass
 *
 * - The trigger's glyph is `chevron.up.chevron.down` ({@link SelectorIcon}),
 *   not a lone `⌄`. iOS uses the double chevron for a control that opens a
 *   *menu of choices* and the single one for a control that expands in place;
 *   this is the former, and the pair is the clearest one-glyph signal in the
 *   whole system that a control is a picker.
 * - The list is the same opaque raised surface as a contextual menu —
 *   `popperMaterial`, full-bleed 44px rows, hairlines between them — because on
 *   iOS a select and a menu *are* the same thing. Two nearly-identical poppers
 *   with different surface or row grammars is what the shared constants exist
 *   to prevent.
 *
 * On the list itself: a checked row is the solid theme-adaptive accent pair,
 * never a wash. `data-highlighted` — the keyboard
 * or pointer cursor — uses the hover channel plus the indigo focus outline, so
 * "where I am" and "what is chosen" differ by hue, not by intensity.
 */

export type SelectOption<T extends string = string> = {
  value: T;
  label: ReactNode;
  /** Second line under the label. Keep it short. */
  hint?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
};

export type SelectProps<T extends string = string> = {
  value: T | null;
  onValueChange: (value: T) => void;
  /** Data-driven list. Use `children` instead when you need groups. */
  options?: readonly SelectOption<T>[];
  children?: ReactNode;
  placeholder?: string;
  size?: ControlSize;
  /** What the control is sitting on — see {@link control}. */
  on?: ControlContext;
  disabled?: boolean;
  invalid?: boolean;
  /** Required when there is no visible `<label>` pointing at this. */
  "aria-label"?: string;
  id?: string;
  /** Off makes the trigger shrink to its content. */
  fullWidth?: boolean;
  className?: string;
  /** Extra classes on the dropdown surface. */
  contentClassName?: string;
};

export function Select<T extends string = string>({
  value,
  onValueChange,
  options,
  children,
  placeholder = "Select…",
  size = "md",
  on = "panel",
  disabled = false,
  invalid = false,
  "aria-label": ariaLabel,
  id,
  fullWidth = true,
  className,
  contentClassName,
}: SelectProps<T>) {
  // Held here only so the phone's Back button can close the list.
  const [open, setOpen] = useState(false);
  useBackClose(open, () => setOpen(false));

  return (
    <SelectPrimitive.Root
      // Radix treats "" as "no value"; the app models that as null.
      value={value ?? undefined}
      onValueChange={(next) => onValueChange(next as T)}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-label={ariaLabel}
        className={control({
          size,
          on,
          invalid,
          fullWidth,
          className: cx(
            "inline-flex items-center justify-between gap-2 text-left",
            // `muted`, not `subtle`, for the same reason `controlBase` uses it:
            // the placeholder is this unlabelled trigger's visible label, so it
            // keeps the stronger quiet-text rung on the field material.
            "data-placeholder:text-muted",
            "pointer-coarse:min-h-tap",
            className,
          ),
        })}
      >
        <span className="min-w-0 truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon asChild>
          {/* `text-faint` is legal here and only here: this is a glyph, not
              text, and faint is the token kept for exactly that (3.55:1). */}
          <SelectorIcon className="size-4 text-faint" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          data-busee-anim="pop"
          // `popper` rather than the default `item-aligned`: item-aligned
          // positioning drags the whole list over the trigger, which on a
          // 7-item lane picker covers the row being edited.
          position="popper"
          sideOffset={6}
          collisionPadding={8}
          className={cx(
            popperMaterial,
            LAYER.overlay,
            "min-w-(--radix-select-trigger-width)",
            contentClassName,
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex h-7 items-center justify-center text-faint pointer-coarse:min-h-tap">
            <ChevronUpIcon className="size-4" />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport className="p-1">
            {options?.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                hint={option.hint}
                icon={option.icon}
                disabled={option.disabled}
              >
                {option.label}
              </SelectItem>
            ))}
            {children}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex h-7 items-center justify-center text-faint pointer-coarse:min-h-tap">
            <ChevronDownIcon className="size-4" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export type SelectItemProps = {
  value: string;
  children: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
};

export function SelectItem({ value, children, hint, icon, disabled }: SelectItemProps) {
  return (
    <SelectPrimitive.Item
      data-busee-menu-row=""
      value={value}
      disabled={disabled}
      className={cx(
        menuRow,
        MENU_TONES.default,
        // `group` so the hint and the tick below can follow the checked state.
        "group",
        // A two-line option may grow; ordinary options remain on the shared row recipe.
        hint ? "py-1" : undefined,
        // Solid accent pair — 5.61:1 light, 8.67:1 dark. The highlight
        // stays on the hover channel, so the two states differ by colour rather
        // than by intensity. No transition: a 150ms crossfade renders two rows
        // selected at once, which is P6.
        "data-[state=checked]:bg-selected data-[state=checked]:text-selected-fg",
        "data-[state=checked]:data-highlighted:bg-selected data-[state=checked]:data-highlighted:text-selected-fg",
      )}
    >
      {icon ? <span className="flex size-5 shrink-0 items-center justify-center">{icon}</span> : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        {hint ? (
          <span className="truncate text-caption text-muted group-data-[state=checked]:text-selected-fg">
            {hint}
          </span>
        ) : null}
      </span>
      {/* Trailing, like every other mark in an iOS menu. The row already carries
          the fill, so the tick is confirmation rather than the whole signal. */}
      <SelectPrimitive.ItemIndicator className="flex size-5 shrink-0 items-center justify-center">
        <CheckIcon className="size-5" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

export function SelectGroup({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <SelectPrimitive.Group>
      <SelectPrimitive.Label
        data-busee-menu-label=""
        className="px-3 pt-3 pb-2 text-caption font-semibold tracking-wide text-subtle"
      >
        {label}
      </SelectPrimitive.Label>
      {children}
    </SelectPrimitive.Group>
  );
}

export function SelectSeparator() {
  return <SelectPrimitive.Separator data-busee-menu-divider="" className="mx-3 h-px bg-separator" />;
}
